import { degrees, PDFDocument, rgb } from "pdf-lib";
import { MAX_FILE_SIZE, ALLOWED_MIME_TYPES } from "./constants/documents";

const WATERMARK_TEXT = "IMPORTE VIA QALIMO";

/**
 * Valide un fichier uploadé (taille et type MIME).
 * @param {File} file - Le fichier à valider
 * @returns {string|null} Message d'erreur si invalide, null si valide
 */
export function validateUploadedFile(file) {
  // Vérification de la taille
  if (file.size > MAX_FILE_SIZE) {
    const maxSizeMo = MAX_FILE_SIZE / (1024 * 1024);
    return `Le fichier "${file.name}" est trop volumineux (max ${maxSizeMo} Mo)`;
  }

  // Vérification du type MIME
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return `Le type de fichier "${file.name}" n'est pas autorisé (PNG, JPG, JPEG, PDF uniquement)`;
  }

  return null;
}

/** Erreur de prétraitement d'un fichier côté navigateur, affichable telle quelle. */
function fileProcessingError(message) {
  const error = new Error(message);
  error.displayMessage = true;
  return error;
}

export function preprocessImage(file, shouldWatermark) {
  return new Promise((resolve, reject) => {
    const fail = () =>
      reject(
        fileProcessingError(
          `Impossible de lire l'image "${file.name}". Vérifiez qu'il s'agit bien d'un PNG ou d'un JPEG.`,
        ),
      );
    const reader = new FileReader();
    reader.onerror = fail;
    reader.onload = (event) => {
      const img = new Image();
      img.onerror = fail;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxWidth = 800;
        const maxHeight = 800;
        let width = img.width;
        let height = img.height;

        // Resize image
        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Add watermark
        if (shouldWatermark) {
          ctx.save(); // Sauvegarde l'état actuel du contexte
          ctx.translate(canvas.width / 2, canvas.height / 2); // Déplace le point d'origine au centre du canvas
          ctx.rotate((45 * Math.PI) / 180);
          ctx.font = "60px Arial";
          ctx.fillStyle = "rgba(0.5, 0.5, 0.5, 0.5)";
          ctx.textAlign = "center";
          ctx.fillText(WATERMARK_TEXT, 0, 0); // Dessine le texte au centre
          ctx.restore(); // Restaure l'état initial du contexte
        }

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              fail();
              return;
            }
            resolve(new File([blob], file.name, { type: file.type }));
          },
          file.type,
          0.8, // Qualité de l'image (80%)
        );
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export function watermarkPdf(file) {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    const fail = () =>
      reject(
        fileProcessingError(
          `Impossible de lire le PDF "${file.name}". Vérifiez qu'il n'est pas endommagé.`,
        ),
      );
    reader.onerror = fail;
    reader.onload = async (event) => {
      try {
        const pdfDoc = await PDFDocument.load(event.target.result, {
          ignoreEncryption: true,
        });

        pdfDoc.getPages().forEach((page) => {
          const { height } = page.getSize();

          page.drawText(WATERMARK_TEXT, {
            x: 50,
            y: height - 200,
            size: 60,
            color: rgb(0.5, 0.5, 0.5),
            opacity: 0.5,
            rotate: degrees(-45),
          });
        });

        const pdfBytes = await pdfDoc.save();
        resolve(new File([pdfBytes], file.name, { type: file.type }));
      } catch (error) {
        console.error(error);
        fail();
      }
    };
    reader.readAsArrayBuffer(file);
  });
}
